import React, { useState } from 'react';
import { DetectAbnormalLayoutResponse } from '../types';

interface DetectAbnormalLayoutResultsProps {
  results: DetectAbnormalLayoutResponse;
  onDownload: (url: string) => void;
}

export const DetectAbnormalLayoutResults: React.FC<DetectAbnormalLayoutResultsProps> = ({ 
  results, 
  onDownload 
}) => {
  const { result, responses, download_url } = results;

  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    summary: false,
    detailedResponses: false
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Calculate total tokens from all responses
  const totalTokens = responses.reduce((acc, response) => {
    // Add tokens from main response
    let inputTokens = response.token_count.input_token;
    let outputTokens = response.token_count.output_token;
    
    // Add tokens from tool result metadata in messages
    if (response.messages) {
      response.messages.forEach(message => {
        if (message.tool_result_metadata?.token_count) {
          inputTokens += message.tool_result_metadata.token_count.input_token || 0;
          outputTokens += message.tool_result_metadata.token_count.output_token || 0;
        }
      });
    }
    
    return {
      input: acc.input + inputTokens,
      output: acc.output + outputTokens
    };
  }, { input: 0, output: 0 });

  return (
    <div className="results">
      <div className="results-header">
        <h3>Abnormal Layout Detection Results</h3>
        <div className="results-header-actions">
          <div className={`fraud-status ${result.is_abnormal_layout ? 'fraud-detected' : 'no-fraud'}`}>
            {result.is_abnormal_layout ? '⚠️ Abnormal Layout Detected' : '✓ Normal Layout'}
          </div>
          <button
            className="btn btn-success"
            onClick={() => onDownload(download_url)}
          >
            Download Analysis Report
          </button>
        </div>
      </div>

      {/* Detection Result Section */}
      <div className="results-section">
        <div className="section-content">
          <div className={`detection-result-card ${result.is_abnormal_layout ? 'abnormal' : 'normal'}`}>
            <div className="detection-status">
              <div className="status-icon">
                {result.is_abnormal_layout ? '⚠️' : '✓'}
              </div>
              <div className="status-text">
                <h4>{result.is_abnormal_layout ? 'Abnormal Layout Detected' : 'Normal Layout'}</h4>
                <p>{result.is_abnormal_layout 
                  ? 'The input image shows significant differences from the reference samples.' 
                  : 'The input image appears consistent with the reference samples.'}
                </p>
              </div>
            </div>
            
            {result.details && (
              <div className="detection-details">
                <h5>Analysis Details:</h5>
                <p className="details-text">{result.details}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Summary Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('summary')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.summary ? 'collapsed' : ''}`}
            aria-label={expandedSections.summary ? 'Collapse summary section' : 'Expand summary section'}
          >
            <span>▼</span>
          </button>
          <h4>Token Usage Summary</h4>
        </div>
        {expandedSections.summary && (
          <div className="section-content">
            <div className="token-usage">
              <div className="token-item">
                <span className="token-label">Total Input Tokens:</span>
                <span className="token-value">{totalTokens.input.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Output Tokens:</span>
                <span className="token-value">{totalTokens.output.toLocaleString()}</span>
              </div>
              <div className="token-item">
                <span className="token-label">Total Tokens:</span>
                <span className="token-value">{(totalTokens.input + totalTokens.output).toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Detailed Responses Section */}
      <div className="results-section">
        <div 
          className="config-header" 
          onClick={() => toggleSection('detailedResponses')}
        >
          <button 
            type="button" 
            className={`collapse-toggle ${!expandedSections.detailedResponses ? 'collapsed' : ''}`}
            aria-label={expandedSections.detailedResponses ? 'Collapse detailed responses section' : 'Expand detailed responses section'}
          >
            <span>▼</span>
          </button>
          <h4>Detailed LLM Responses ({responses.length})</h4>
        </div>
        {expandedSections.detailedResponses && (
          <div className="section-content">
            <div className="response-group">
              {responses.map((response, index) => (
                <div key={index} className="response-card">
                  <div className="response-header">
                    <strong>Response {index + 1}</strong>
                    <div>
                      Model: {response.model_info.model_name} ({response.model_info.provider})
                    </div>
                    <div>
                      Tokens: {response.token_count.input_token.toLocaleString()} in / {response.token_count.output_token.toLocaleString()} out
                    </div>
                    <div>
                      Processing Time: {response.processing_time.toFixed(2)}s
                    </div>
                  </div>
                  <div className="response-details">
                    <details open>
                      <summary>View Raw Response</summary>
                      <pre className="raw-text">{(() => {
                        // Priority: raw_value > value > tool_calls
                        if (response.raw_value) {
                          return response.raw_value;
                        }
                        if (response.value) {
                          return typeof response.value === 'string' 
                            ? response.value 
                            : JSON.stringify(response.value, null, 2);
                        }
                        // If no value but has tool calls, show tool calls
                        if (response.tool_calls && response.tool_calls.length > 0) {
                          return JSON.stringify(response.tool_calls, null, 2);
                        }
                        return '(No response content)';
                      })()}</pre>
                    </details>
                    {response.tool_calls && response.tool_calls.length > 0 && (
                      <details>
                        <summary>View Tool Calls ({response.tool_calls.length})</summary>
                        <div className="tool-calls-list">
                          {response.tool_calls.map((toolCall, idx) => (
                            <div key={idx} className="tool-call-card">
                              <div className="tool-call-header">
                                <span className="tool-call-type">{toolCall.type}</span>
                                {toolCall.name && <span className="tool-call-name">{toolCall.name}</span>}
                              </div>
                              {toolCall.id && (
                                <div className="tool-call-id">
                                  <strong>ID:</strong> {toolCall.id}
                                </div>
                              )}
                              {toolCall.args && Object.keys(toolCall.args).length > 0 && (
                                <div className="tool-call-section">
                                  <div className="tool-call-section-title">Arguments:</div>
                                  <div className="tool-call-args">
                                    {Object.entries(toolCall.args).map(([key, value]) => (
                                      <div key={key} className="tool-call-arg-item">
                                        <span className="arg-key">{key}:</span>
                                        <span className="arg-value">
                                          {typeof value === 'object' 
                                            ? JSON.stringify(value, null, 2) 
                                            : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {toolCall.extra_content && Object.keys(toolCall.extra_content).length > 0 && (
                                <div className="tool-call-section">
                                  <div className="tool-call-section-title">Extra Content:</div>
                                  <pre className="raw-text">{JSON.stringify(toolCall.extra_content, null, 2)}</pre>
                                </div>
                              )}
                              <div className="tool-call-section">
                                <details>
                                  <summary>View Full JSON</summary>
                                  <pre className="raw-text">{JSON.stringify(toolCall, null, 2)}</pre>
                                </details>
                              </div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {response.prompt && (
                      <details>
                        <summary>View Prompt</summary>
                        <pre className="raw-text">{response.prompt}</pre>
                      </details>
                    )}
                    {response.messages && response.messages.length > 0 && (
                      <details>
                        <summary>View Messages ({response.messages.length})</summary>
                        <div className="messages-list">
                          {response.messages.map((message, msgIdx) => (
                            <div key={msgIdx} className="message-card">
                              <div className="message-header">
                                <strong>Message {msgIdx + 1}</strong>
                                {message.is_tool_result && <span className="message-tag tool-result-tag">Tool Result</span>}
                              </div>
                              {message.tool_result_metadata && (
                                <div className="tool-result-metadata">
                                  <div className="metadata-label">Tool Result Metadata:</div>
                                  {message.tool_result_metadata.token_count && (
                                    <div className="metadata-tokens">
                                      <span className="metadata-item">
                                        Input Tokens: {message.tool_result_metadata.token_count.input_token.toLocaleString()}
                                      </span>
                                      <span className="metadata-item">
                                        Output Tokens: {message.tool_result_metadata.token_count.output_token.toLocaleString()}
                                      </span>
                                    </div>
                                  )}
                                  {message.tool_result_metadata.raw_value && (
                                    <details>
                                      <summary>View Raw Value</summary>
                                      <pre className="raw-text">{message.tool_result_metadata.raw_value}</pre>
                                    </details>
                                  )}
                                </div>
                              )}
                              <details>
                                <summary>View Full Message JSON</summary>
                                <pre className="raw-text">{JSON.stringify(message, null, 2)}</pre>
                              </details>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
